import React, { useContext, useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    useToast,
    Card,
    CardBody,
    Flex,
    SimpleGrid,
    Spinner,
    Input,
    Button,
    Badge,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    Icon,
    Divider,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Select,
    FormControl,
    FormLabel,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    TableContainer,
    Alert,
    AlertIcon,
    AlertDescription,
} from '@chakra-ui/react';
import {
    FaFileAlt,
    FaDownload,
    FaUser,
    FaChartBar,
    FaShieldAlt,
    FaFilter,
    FaCalendar
} from 'react-icons/fa';
import Navbar from '../compnents/Navbar';
import SideBar from '../compnents/SideBar';
import Footer from '../compnents/Footer';
import AuthContext from '../context/AuthContext';
import ThemeContext from '../context/ThemeContext';
import UploadYourFile from './UploadYourFile';
import Loading from '../compnents/Loading';
import EmptyStateCard from '../compnents/EmptyStateCard';
import WorkspaceHero from '../compnents/WorkspaceHero';
import SecurityPostureCard from '../compnents/SecurityPostureCard';
import DataProtectionRequests from '../compnents/DataProtectionRequests';
import { useNavigate } from 'react-router-dom';
import { getWorkspaceSetupSummary } from '../content/tenantWorkspaceSetup';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

function AuditCompliance() {
    const { token, user, isAuthInitializing } = useContext(AuthContext);
    const { themeData } = useContext(ThemeContext);
    const [auditLogs, setAuditLogs] = useState([]);
    const [auditStats, setAuditStats] = useState(null);
    const [userActivity, setUserActivity] = useState(null);
    const [complianceReport, setComplianceReport] = useState(null);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [loadingStats, setLoadingStats] = useState(false);
    const [loadingActivity, setLoadingActivity] = useState(false);
    const [loadingReport, setLoadingReport] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, page_size: 50, total: 0 });
    const [filters, setFilters] = useState({
        user_id: '',
        action: '',
        resource_type: '',
        resource_id: '',
        from_date: '',
        to_date: '',
    });
    const [selectedUserId, setSelectedUserId] = useState('');
    // eslint-disable-next-line
    const [activityDays, setActivityDays] = useState(30);
    const [statsDays, setStatsDays] = useState(30);
    const [exportFormat, setExportFormat] = useState('json');
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isActivityOpen, onOpen: onActivityOpen, onClose: onActivityClose } = useDisclosure();
    const { isOpen: isEmployeeListOpen, onOpen: onEmployeeListOpen, onClose: onEmployeeListClose } = useDisclosure();
    const [employees, setEmployees] = useState([]);
    const [loadingEmployees, setLoadingEmployees] = useState(false);
    const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
    const navigate = useNavigate();

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#0B2545';
    const secondaryColor = themeData?.colors?.secondary || '#13315C';
    const accentColor = themeData?.colors?.accent || '#13C1BB';
    const backgroundColor = themeData?.colors?.background || '#F5F7FA';

    // Determine if theme is dark (midnight)
    const isDarkTheme = themeData?.id === 'midnight';

    // Theme-aware colors
    const headingTextColor = isDarkTheme ? '#F5F7FA' : primaryColor;
    const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.700';
    const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.600';
    const strongTextColor = isDarkTheme ? '#F5F7FA' : 'gray.800';
    const labelTextColor = isDarkTheme ? '#E2E8F0' : 'gray.700';
    const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';
    const inputBg = isDarkTheme ? 'rgba(45, 55, 72, 0.4)' : 'white';
    const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200';
    // const activeBorderColor = isDarkTheme ? accentColor : primaryColor;
    const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor;
    const tabSelectedColor = isDarkTheme ? accentColor : primaryColor;
    const tableBg = isDarkTheme ? 'rgba(45, 55, 72, 0.4)' : 'gray.50';
    const tableHoverBg = isDarkTheme ? 'rgba(66, 153, 225, 0.08)' : 'gray.100';
    const alertBg = isDarkTheme ? 'rgba(66, 153, 225, 0.12)' : 'brand.tealSoft';
    const setupSummary = getWorkspaceSetupSummary(user);

    // Fetch audit logs
    const fetchAuditLogs = async () => {
        if (!token) return;

        try {
            setLoadingLogs(true);
            const params = {
                page: pagination.page,
                page_size: pagination.page_size,
                ...Object.fromEntries(
                    Object.entries(filters).filter(([_, value]) => value !== '')
                ),
            };

            const res = await axios.get(`${API_BASE_URL}/content/admin-audit-logs`, {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });

            setAuditLogs(res.data.logs || []);
            setPagination((prev) => ({
                ...prev,
                total: res.data.total_count || 0,
            }));
        } catch (error) {
            console.error('Error fetching audit logs:', error);
            toast({
                title: 'Error loading audit logs',
                description: error.response?.data?.message || 'Please try again',
                status: 'error',
                duration: 3000,
            });
        } finally {
            setLoadingLogs(false);
        }
    };

    // Fetch audit statistics
    const fetchAuditStats = async () => {
        if (!token) return;

        try {
            setLoadingStats(true);
            const res = await axios.get(`${API_BASE_URL}/content/admin-audit-stats`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { days: statsDays },
            });

            setAuditStats(res.data);
        } catch (error) {
            console.error('Error fetching audit stats:', error);
            toast({
                title: 'Error loading statistics',
                description: error.response?.data?.message || 'Please try again',
                status: 'error',
                duration: 3000,
            });
        } finally {
            setLoadingStats(false);
        }
    };

    // Fetch user activity
    const fetchUserActivity = async (userId) => {
        if (!token || !userId) return;

        try {
            setLoadingActivity(true);
            const res = await axios.get(
                `${API_BASE_URL}/content/admin-user-activity/${userId}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { days: activityDays },
                }
            );

            setUserActivity(res.data);
            onActivityOpen();
        } catch (error) {
            console.error('Error fetching user activity:', error);
            toast({
                title: 'Error loading user activity',
                description: error.response?.data?.message || 'Please try again',
                status: 'error',
                duration: 3000,
            });
        } finally {
            setLoadingActivity(false);
        }
    };

    // Generate compliance report
    const generateComplianceReport = async () => {
        if (!token) return;

        if (!filters.from_date || !filters.to_date) {
            toast({
                title: 'Date range required',
                description: 'Please select both start and end dates',
                status: 'warning',
                duration: 3000,
            });
            return;
        }

        try {
            setLoadingReport(true);
            const res = await axios.get(
                `${API_BASE_URL}/content/admin-audit-compliance-report`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params: {
                        from_date: filters.from_date,
                        to_date: filters.to_date,
                    },
                }
            );

            setComplianceReport(res.data);
            onOpen();
        } catch (error) {
            console.error('Error generating compliance report:', error);
            toast({
                title: 'Error generating report',
                description: error.response?.data?.message || 'Please try again',
                status: 'error',
                duration: 3000,
            });
        } finally {
            setLoadingReport(false);
        }
    };

    // Export audit logs
    const exportAuditLogs = async (format) => {
        if (!token) return;

        try {
            setExporting(true);
            const exportData = {
                format,
                ...Object.fromEntries(
                    Object.entries(filters).filter(([_, value]) => value !== '')
                ),
            };

            const res = await axios.post(
                `${API_BASE_URL}/content/admin-audit-export`,
                exportData,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: format === 'csv' ? 'blob' : 'json',
                }
            );

            // Create download link
            const blob = new Blob([format === 'csv' ? res.data : JSON.stringify(res.data, null, 2)], {
                type: format === 'csv' ? 'text/csv' : 'application/json',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `audit_logs_${Date.now()}.${format}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast({
                title: 'Export successful',
                description: `Audit logs exported as ${format.toUpperCase()}`,
                status: 'success',
                duration: 3000,
            });
        } catch (error) {
            console.error('Error exporting audit logs:', error);
            toast({
                title: 'Error exporting logs',
                description: error.response?.data?.message || 'Please try again',
                status: 'error',
                duration: 3000,
            });
        } finally {
            setExporting(false);
        }
    };

    // Load initial data
    useEffect(() => {
        const hasAccess = user && user.subscription === 'business' &&
            (user.role === 'admin' || user.role === 'compliance');
        if (hasAccess) {
            fetchAuditLogs();
            fetchAuditStats();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, pagination.page, pagination.page_size]);

    // Fetch all employees
    const fetchEmployees = async () => {
        if (!token) return;

        try {
            setLoadingEmployees(true);
            const res = await axios.get(`${API_BASE_URL}/roles/list-all-employees`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setEmployees(res.data.employees || res.data || []);
        } catch (error) {
            console.error('Error fetching employees:', error);
            toast({
                title: 'Error loading employees',
                description: error.response?.data?.message || 'Please try again',
                status: 'error',
                duration: 3000,
            });
        } finally {
            setLoadingEmployees(false);
        }
    };

    // Copy to clipboard helper
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            toast({
                title: 'Copied!',
                description: 'User ID copied to clipboard',
                status: 'success',
                duration: 2000,
            });
        }).catch((err) => {
            console.error('Failed to copy:', err);
            toast({
                title: 'Copy failed',
                description: 'Please copy manually',
                status: 'error',
                duration: 2000,
            });
        });
    };

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const handleApplyFilters = () => {
        setPagination((prev) => ({ ...prev, page: 1 }));
        fetchAuditLogs();
    };

    const handleClearFilters = () => {
        setFilters({
            user_id: '',
            action: '',
            resource_type: '',
            resource_id: '',
            from_date: '',
            to_date: '',
        });
        setPagination((prev) => ({ ...prev, page: 1 }));
    };

    const hasActiveFilters = Object.values(filters).some((value) => value !== '');
    const filteredEmployees = employees.filter((emp) => {
        const searchLower = employeeSearchTerm.toLowerCase();
        return (
            !employeeSearchTerm ||
            emp.email?.toLowerCase().includes(searchLower) ||
            emp.name?.toLowerCase().includes(searchLower) ||
            emp.mlUserId?.toLowerCase().includes(searchLower)
        );
    });

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    if (isAuthInitializing || !token) {
        return <Loading />;
    }

    if (!user) {
        return (
            <Box
                minH="100vh"
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                bg={backgroundColor}
            >
                <Spinner size="xl" color={isDarkTheme ? accentColor : primaryColor} />
            </Box>
        );
    }

    if (user && user.noOfFileUploads === 0 && user.role === 'admin') {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" bg={backgroundColor}>
                <Navbar />
                <UploadYourFile token={token} />
            </Box>
        );
    }

    if (user && user.noOfFileUploads === 0) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" bg={backgroundColor}>
                <Navbar />
                <Box display="flex" flex="1">
                    <SideBar />
                    <Container maxW="container.xl" flex="1" py={8}>
                        <EmptyStateCard
                            icon={FaFileAlt}
                            title="Audit data appears after the first dataset upload"
                            description="A firm admin needs to upload the first CSV or Excel file before audit logs, activity review, and compliance reports can be generated."
                            hint="Compliance users can inspect the console after the Business tenant has live data."
                            bg={cardBg}
                            borderColor={borderColor}
                            titleColor={strongTextColor}
                            descriptionColor={bodyTextColor}
                            hintColor={mutedTextColor}
                            iconColor={primaryColor}
                        />
                    </Container>
                </Box>
                <Footer />
            </Box>
        );
    }

    // Check if user is compliance (NOT admin)
    // if (user.subscription === 'business' && user.role === 'compliance') {
    //     return (
    //         <Box minH="100vh" display="flex" flexDirection="column">
    //             <Navbar />
    //             <Box display="flex" flex="1" bg={backgroundColor}>
    //                 <SideBar />
    //                 <Container maxW="container.xl" flex="1" py={8}>
    //                     <VStack spacing={6} align="center" justify="center" minH="60vh">
    //                         <Heading size="lg" color={headingTextColor}>
    //                             Hello Compliance Officer. Welcome to Audit page
    //                         </Heading>
    //                         <Text color={bodyTextColor} textAlign="center">
    //                             You have limited access to audit compliance tools.
    //                         </Text>
    //                     </VStack>
    //                 </Container>
    //             </Box>
    //             <Footer />
    //         </Box>
    //     );
    // }

    // Check if user has access
    const hasAccess = user.subscription === 'business' &&
        (user.role === 'admin' || user.role === 'compliance');

    if (!hasAccess) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column">
                <Navbar />
                <Box display="flex" flex="1" bg={backgroundColor}>
                    <SideBar />
                    <Container maxW="container.xl" flex="1" py={8}>
                        <VStack spacing={6} align="center" justify="center" minH="60vh">
                            <Icon as={FaShieldAlt} boxSize={16} color={mutedTextColor} />
                            <Heading size="lg" color={headingTextColor}>
                                Audit & Compliance
                            </Heading>
                            <Text color={bodyTextColor} textAlign="center">
                                This feature is only available for Business subscription users with higher access than yours.
                            </Text>
                        </VStack>
                    </Container>
                </Box>
                <Footer />
            </Box>
        );
    }

    return (
        <Box minH="100vh" display="flex" flexDirection="column">
            <Navbar />
            <Box display="flex" flex="1" bg={backgroundColor}>
                <SideBar />
                <Container maxW="container.xl" flex="1" py={8}>
                    <VStack spacing={8} align="stretch">
                        <WorkspaceHero
                            eyebrow="Compliance console"
                            title="Monitor activity, investigate user behaviour, and generate cleaner compliance outputs."
                            description="Review user activity, filter the audit trail, and export compliance reports for internal review."
                            stats={[
                                { label: 'Access level', value: user.role === 'compliance' ? 'Compliance' : 'Admin', help: 'Current authenticated audit role' },
                                { label: 'Workspace state', value: setupSummary.isOperational ? 'Operational' : 'Provisioning', help: 'Combined data and domain readiness' },
                                { label: 'Report format', value: exportFormat.toUpperCase(), help: 'Current export preference' },
                            ]}
                            bg={cardBg}
                            borderColor={borderColor}
                            titleColor={headingTextColor}
                            descriptionColor={bodyTextColor}
                            accentColor={isDarkTheme ? accentColor : primaryColor}
                            cardBg={tableBg}
                            actions={user.role === 'admin' ? (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    borderColor={borderColor}
                                    color={strongTextColor}
                                    onClick={() => navigate(setupSummary.nextAction.path)}
                                >
                                    {setupSummary.nextAction.label}
                                </Button>
                            ) : null}
                        />

                        {user.role === 'admin' && !setupSummary.isOperational ? (
                            <Alert
                                status="info"
                                borderRadius="xl"
                                bg={alertBg}
                                borderWidth="1px"
                                borderColor={borderColor}
                            >
                                <AlertIcon />
                                <AlertDescription color={bodyTextColor}>
                                    {setupSummary.nextAction.help}
                                </AlertDescription>
                            </Alert>
                        ) : null}

                        {/* Statistics Dashboard */}
                        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
                            <CardBody>
                                <VStack spacing={4} align="stretch">
                                    <Flex justify="space-between" align="center">
                                        <Heading size="md" color={headingTextColor}>
                                            <Icon as={FaChartBar} mr={2} />
                                            Statistics Overview
                                        </Heading>
                                        <HStack>
                                            <Select
                                                size="sm"
                                                value={statsDays}
                                                onChange={(e) => setStatsDays(Number(e.target.value))}
                                                bg={inputBg}
                                                color={strongTextColor}
                                                borderColor={borderColor}
                                                width="120px"
                                            >
                                                <option value={7}>Last 7 days</option>
                                                <option value={30}>Last 30 days</option>
                                                <option value={90}>Last 90 days</option>
                                            </Select>
                                            <Button
                                                size="sm"
                                                onClick={fetchAuditStats}
                                                isLoading={loadingStats}
                                                bg={primaryColor}
                                                color="white"
                                                _hover={{ bg: buttonHoverBg }}
                                            >
                                                Refresh
                                            </Button>
                                        </HStack>
                                    </Flex>

                                    {loadingStats ? (
                                        <Flex justify="center" py={8}>
                                            <Spinner color={primaryColor} />
                                        </Flex>
                                    ) : auditStats ? (
                                        <SimpleGrid columns={{ base: 1, md: 2, lg: 2 }} spacing={4}>
                                            <Stat
                                                p={4}
                                                bg={tableBg}
                                                borderRadius="md"
                                                borderWidth="1px"
                                                borderColor={borderColor}
                                            >
                                                <StatLabel color={labelTextColor}>Total Actions</StatLabel>
                                                <StatNumber color={strongTextColor}>
                                                    {auditStats.total_actions || 0}
                                                </StatNumber>
                                                <StatHelpText color={mutedTextColor}>
                                                    Last {statsDays} days
                                                </StatHelpText>
                                            </Stat>
                                            <Stat
                                                p={4}
                                                bg={tableBg}
                                                borderRadius="md"
                                                borderWidth="1px"
                                                borderColor={borderColor}
                                            >
                                                <StatLabel color={labelTextColor}>Unique Users</StatLabel>
                                                <StatNumber color={strongTextColor}>
                                                    {auditStats.unique_users || 0}
                                                </StatNumber>
                                                <StatHelpText color={mutedTextColor}>Active users</StatHelpText>
                                            </Stat>
                                            {/* <Stat
                                                p={4}
                                                bg={tableBg}
                                                borderRadius="md"
                                                borderWidth="1px"
                                                borderColor={borderColor}
                                            >
                                                <StatLabel color={labelTextColor}>Most Common Action</StatLabel>
                                                <StatNumber color={strongTextColor} fontSize="lg">
                                                    {auditStats.most_common_action || 'N/A'}
                                                </StatNumber>
                                                <StatHelpText color={mutedTextColor}>
                                                    {auditStats.action_count || 0} times
                                                </StatHelpText>
                                            </Stat>
                                            <Stat
                                                p={4}
                                                bg={tableBg}
                                                borderRadius="md"
                                                borderWidth="1px"
                                                borderColor={borderColor}
                                            >
                                                <StatLabel color={labelTextColor}>Resource Types</StatLabel>
                                                <StatNumber color={strongTextColor}>
                                                    {auditStats.resource_types_count || 0}
                                                </StatNumber>
                                                <StatHelpText color={mutedTextColor}>Different types</StatHelpText>
                                            </Stat> */}
                                        </SimpleGrid>
                                    ) : (
                                        <Text color={bodyTextColor} textAlign="center" py={4}>
                                            No statistics available
                                        </Text>
                                    )}
                                </VStack>
                            </CardBody>
                        </Card>

                        <SecurityPostureCard
                            cardBg={cardBg}
                            borderColor={borderColor}
                            headingTextColor={headingTextColor}
                            bodyTextColor={bodyTextColor}
                            mutedTextColor={mutedTextColor}
                        />

                        <DataProtectionRequests
                            apiBaseUrl={API_BASE_URL}
                            token={token}
                            cardBg={cardBg}
                            borderColor={borderColor}
                            headingTextColor={headingTextColor}
                            bodyTextColor={bodyTextColor}
                            mutedTextColor={mutedTextColor}
                        />

                        <Tabs colorScheme="blue" variant="enclosed">
                            <TabList borderColor={borderColor}>
                                <Tab
                                    color={bodyTextColor}
                                    _selected={{
                                        color: tabSelectedColor,
                                        borderColor: tabSelectedColor,
                                        bg: cardBg,
                                    }}
                                >
                                    <Icon as={FaFileAlt} mr={2} />
                                    Audit Logs
                                </Tab>
                                <Tab
                                    color={bodyTextColor}
                                    _selected={{
                                        color: tabSelectedColor,
                                        borderColor: tabSelectedColor,
                                        bg: cardBg,
                                    }}
                                >
                                    <Icon as={FaShieldAlt} mr={2} />
                                    Compliance Reports
                                </Tab>
                            </TabList>

                            <TabPanels>
                                {/* Audit Logs Tab */}
                                <TabPanel px={0}>
                                    <VStack spacing={6} align="stretch">
                                        {/* Filters */}
                                        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
                                            <CardBody>
                                                <VStack spacing={4} align="stretch">
                                                    <Flex justify="space-between" align="center">
                                                        <Heading size="sm" color={headingTextColor}>
                                                            <Icon as={FaFilter} mr={2} />
                                                            Filters
                                                        </Heading>
                                                        <HStack>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                borderColor={borderColor}
                                                                color={strongTextColor}
                                                                onClick={handleClearFilters}
                                                            >
                                                                Clear
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                bg={primaryColor}
                                                                color="white"
                                                                onClick={handleApplyFilters}
                                                                _hover={{ bg: buttonHoverBg }}
                                                            >
                                                                Apply Filters
                                                            </Button>
                                                        </HStack>
                                                    </Flex>

                                                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                                                        <FormControl>
                                                            <FormLabel fontSize="sm" color={labelTextColor}>
                                                                User ID
                                                            </FormLabel>
                                                            <HStack>
                                                                <Input
                                                                    size="sm"
                                                                    value={filters.user_id}
                                                                    onChange={(e) =>
                                                                        handleFilterChange('user_id', e.target.value)
                                                                    }
                                                                    placeholder="Filter by user ID"
                                                                    bg={inputBg}
                                                                    color={strongTextColor}
                                                                    borderColor={borderColor}
                                                                />
                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        fetchEmployees();
                                                                        onEmployeeListOpen();
                                                                    }}
                                                                    variant="outline"
                                                                    borderColor={borderColor}
                                                                    color={primaryColor}
                                                                    _hover={{ bg: buttonHoverBg, color: 'white' }}
                                                                    flexShrink={0}
                                                                >
                                                                    <Icon as={FaUser} />
                                                                </Button>
                                                            </HStack>
                                                        </FormControl>
                                                        <FormControl>
                                                            <FormLabel fontSize="sm" color={labelTextColor}>
                                                                Action
                                                            </FormLabel>
                                                            <Input
                                                                size="sm"
                                                                value={filters.action}
                                                                onChange={(e) =>
                                                                    handleFilterChange('action', e.target.value)
                                                                }
                                                                placeholder="e.g., create, update, delete"
                                                                bg={inputBg}
                                                                color={strongTextColor}
                                                                borderColor={borderColor}
                                                            />
                                                        </FormControl>
                                                        <FormControl>
                                                            <FormLabel fontSize="sm" color={labelTextColor}>
                                                                Resource Type
                                                            </FormLabel>
                                                            <Input
                                                                size="sm"
                                                                value={filters.resource_type}
                                                                onChange={(e) =>
                                                                    handleFilterChange('resource_type', e.target.value)
                                                                }
                                                                placeholder="e.g., document, user"
                                                                bg={inputBg}
                                                                color={strongTextColor}
                                                                borderColor={borderColor}
                                                            />
                                                        </FormControl>
                                                        <FormControl>
                                                            <FormLabel fontSize="sm" color={labelTextColor}>
                                                                Resource ID
                                                            </FormLabel>
                                                            <Input
                                                                size="sm"
                                                                value={filters.resource_id}
                                                                onChange={(e) =>
                                                                    handleFilterChange('resource_id', e.target.value)
                                                                }
                                                                placeholder="Specific resource ID"
                                                                bg={inputBg}
                                                                color={strongTextColor}
                                                                borderColor={borderColor}
                                                            />
                                                        </FormControl>
                                                        <FormControl>
                                                            <FormLabel fontSize="sm" color={labelTextColor}>
                                                                From Date
                                                            </FormLabel>
                                                            <Input
                                                                size="sm"
                                                                type="datetime-local"
                                                                value={filters.from_date}
                                                                onChange={(e) =>
                                                                    handleFilterChange('from_date', e.target.value)
                                                                }
                                                                bg={inputBg}
                                                                color={strongTextColor}
                                                                borderColor={borderColor}
                                                            />
                                                        </FormControl>
                                                        <FormControl>
                                                            <FormLabel fontSize="sm" color={labelTextColor}>
                                                                To Date
                                                            </FormLabel>
                                                            <Input
                                                                size="sm"
                                                                type="datetime-local"
                                                                value={filters.to_date}
                                                                onChange={(e) =>
                                                                    handleFilterChange('to_date', e.target.value)
                                                                }
                                                                bg={inputBg}
                                                                color={strongTextColor}
                                                                borderColor={borderColor}
                                                            />
                                                        </FormControl>
                                                    </SimpleGrid>
                                                </VStack>
                                            </CardBody>
                                        </Card>

                                        {/* Export Options */}
                                        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
                                            <CardBody>
                                                <Flex justify="space-between" align="center">
                                                    <Heading size="sm" color={headingTextColor}>
                                                        <Icon as={FaDownload} mr={2} />
                                                        Export Logs
                                                    </Heading>
                                                    <HStack>
                                                        <Select
                                                            size="sm"
                                                            value={exportFormat}
                                                            onChange={(e) => setExportFormat(e.target.value)}
                                                            width="120px"
                                                            bg={inputBg}
                                                            color={strongTextColor}
                                                            borderColor={borderColor}
                                                        >
                                                            <option value="json">JSON</option>
                                                            <option value="csv">CSV</option>
                                                        </Select>
                                                        <Button
                                                            size="sm"
                                                            leftIcon={<FaDownload />}
                                                            onClick={() => exportAuditLogs(exportFormat)}
                                                            isLoading={exporting}
                                                            bg={primaryColor}
                                                            color="white"
                                                            _hover={{ bg: buttonHoverBg }}
                                                        >
                                                            Export
                                                        </Button>
                                                    </HStack>
                                                </Flex>
                                            </CardBody>
                                        </Card>

                                        {/* Audit Logs Table */}
                                        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} maxW="100vw">
                                            <CardBody>
                                                <VStack spacing={4} align="stretch">
                                                    <Heading size="sm" color={headingTextColor}>
                                                        Audit Log Entries
                                                    </Heading>

                                                    {loadingLogs ? (
                                                        <Flex justify="center" py={8}>
                                                            <Spinner color={primaryColor} />
                                                        </Flex>
                                                    ) : auditLogs.length > 0 ? (
                                                        <>
                                                            <TableContainer>
                                                                <Table size="sm" variant="simple">
                                                                    <Thead bg={tableBg}>
                                                                        <Tr>
                                                                            <Th color={labelTextColor}>Timestamp</Th>
                                                                            <Th color={labelTextColor}>User ID</Th>
                                                                            <Th color={labelTextColor}>Action</Th>
                                                                            <Th color={labelTextColor}>Resource</Th>
                                                                            <Th color={labelTextColor}>Details</Th>
                                                                        </Tr>
                                                                    </Thead>
                                                                    <Tbody>
                                                                        {auditLogs.map((log, index) => (
                                                                            <Tr
                                                                                key={log.id || index}
                                                                                _hover={{ bg: tableHoverBg }}
                                                                            >
                                                                                <Td color={bodyTextColor}>
                                                                                    {formatDate(log.timestamp)}
                                                                                </Td>
                                                                                <Td>
                                                                                    <HStack>
                                                                                        <Text color={bodyTextColor}>
                                                                                            {log.user_id}
                                                                                        </Text>
                                                                                        <Button
                                                                                            size="xs"
                                                                                            variant="ghost"
                                                                                            onClick={() => {
                                                                                                setSelectedUserId(log.user_id);
                                                                                                fetchUserActivity(log.user_id);
                                                                                            }}
                                                                                            color={primaryColor}
                                                                                        >
                                                                                            <Icon as={FaUser} />
                                                                                        </Button>
                                                                                    </HStack>
                                                                                </Td>
                                                                                <Td>
                                                                                    <Badge colorScheme="blue">
                                                                                        {log.action}
                                                                                    </Badge>
                                                                                </Td>
                                                                                <Td color={bodyTextColor}>
                                                                                    <Text fontSize="xs">
                                                                                        {log.resource_type}
                                                                                        {log.resource_id && ` (${log.resource_id})`}
                                                                                    </Text>
                                                                                </Td>
                                                                                <Td color={mutedTextColor} fontSize="xs">
                                                                                    {log.details || log.ip_address || 'N/A'}
                                                                                </Td>
                                                                            </Tr>
                                                                        ))}
                                                                    </Tbody>
                                                                </Table>
                                                            </TableContainer>

                                                            {/* Pagination */}
                                                            <Flex justify="space-between" align="center" mt={4}>
                                                                <Text fontSize="sm" color={bodyTextColor}>
                                                                    Showing {(pagination.page - 1) * pagination.page_size + 1} to{' '}
                                                                    {Math.min(pagination.page * pagination.page_size, pagination.total)}{' '}
                                                                    of {pagination.total} entries
                                                                </Text>
                                                                <HStack>
                                                                    <Button
                                                                        size="sm"
                                                                        onClick={() =>
                                                                            setPagination((prev) => ({
                                                                                ...prev,
                                                                                page: prev.page - 1,
                                                                            }))
                                                                        }
                                                                        isDisabled={pagination.page === 1}
                                                                        bg={primaryColor}
                                                                        color="white"
                                                                        _hover={{ bg: buttonHoverBg }}
                                                                    >
                                                                        Previous
                                                                    </Button>
                                                                    <Text fontSize="sm" color={bodyTextColor}>
                                                                        Page {pagination.page} of{' '}
                                                                        {Math.ceil(pagination.total / pagination.page_size)}
                                                                    </Text>
                                                                    <Button
                                                                        size="sm"
                                                                        onClick={() =>
                                                                            setPagination((prev) => ({
                                                                                ...prev,
                                                                                page: prev.page + 1,
                                                                            }))
                                                                        }
                                                                        isDisabled={
                                                                            pagination.page >=
                                                                            Math.ceil(pagination.total / pagination.page_size)
                                                                        }
                                                                        bg={primaryColor}
                                                                        color="white"
                                                                        _hover={{ bg: buttonHoverBg }}
                                                                    >
                                                                        Next
                                                                    </Button>
                                                                </HStack>
                                                            </Flex>
                                                        </>
                                                    ) : (
                                                        <EmptyStateCard
                                                            icon={FaFileAlt}
                                                            title={hasActiveFilters ? 'No audit logs match these filters' : 'Audit logs will appear here once activity starts'}
                                                            description={
                                                                hasActiveFilters
                                                                    ? 'Try broadening the selected user, action, resource, or date range to see more activity.'
                                                                    : 'User actions, access events, and content operations will be collected here for review.'
                                                            }
                                                            hint={
                                                                hasActiveFilters
                                                                    ? 'Clear the current filters to return to the full log view.'
                                                                    : 'Come back after users start working in the platform or generate a compliance export.'
                                                            }
                                                            bg={tableBg}
                                                            borderColor={borderColor}
                                                            titleColor={headingTextColor}
                                                            descriptionColor={bodyTextColor}
                                                            hintColor={mutedTextColor}
                                                            iconColor={isDarkTheme ? accentColor : primaryColor}
                                                            maxW="full"
                                                        >
                                                            {hasActiveFilters ? (
                                                                <Button
                                                                    variant="outline"
                                                                    borderColor={primaryColor}
                                                                    color={primaryColor}
                                                                    _hover={{ bg: tableHoverBg }}
                                                                    onClick={handleClearFilters}
                                                                >
                                                                    Clear filters
                                                                </Button>
                                                            ) : (
                                                                <Button
                                                                    bg={primaryColor}
                                                                    color="white"
                                                                    _hover={{ bg: buttonHoverBg }}
                                                                    onClick={fetchAuditLogs}
                                                                >
                                                                    Refresh logs
                                                                </Button>
                                                            )}
                                                        </EmptyStateCard>
                                                    )}
                                                </VStack>
                                            </CardBody>
                                        </Card>
                                    </VStack>
                                </TabPanel>

                                {/* Compliance Reports Tab */}
                                <TabPanel px={0}>
                                    <VStack spacing={6} align="stretch">
                                        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
                                            <CardBody>
                                                <VStack spacing={4} align="stretch">
                                                    <Heading size="md" color={headingTextColor}>
                                                        <Icon as={FaCalendar} mr={2} />
                                                        Generate Compliance Report
                                                    </Heading>
                                                    <Text color={bodyTextColor} fontSize="sm">
                                                        Select a date range to generate a comprehensive compliance report for
                                                        regulatory requirements.
                                                    </Text>

                                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                                        <FormControl>
                                                            <FormLabel fontSize="sm" color={labelTextColor}>
                                                                From Date
                                                            </FormLabel>
                                                            <Input
                                                                type="datetime-local"
                                                                value={filters.from_date}
                                                                onChange={(e) =>
                                                                    handleFilterChange('from_date', e.target.value)
                                                                }
                                                                bg={inputBg}
                                                                color={strongTextColor}
                                                                borderColor={borderColor}
                                                            />
                                                        </FormControl>
                                                        <FormControl>
                                                            <FormLabel fontSize="sm" color={labelTextColor}>
                                                                To Date
                                                            </FormLabel>
                                                            <Input
                                                                type="datetime-local"
                                                                value={filters.to_date}
                                                                onChange={(e) =>
                                                                    handleFilterChange('to_date', e.target.value)
                                                                }
                                                                bg={inputBg}
                                                                color={strongTextColor}
                                                                borderColor={borderColor}
                                                            />
                                                        </FormControl>
                                                    </SimpleGrid>

                                                    <Button
                                                        leftIcon={<FaShieldAlt />}
                                                        onClick={generateComplianceReport}
                                                        isLoading={loadingReport}
                                                        bg={primaryColor}
                                                        color="white"
                                                        _hover={{ bg: buttonHoverBg }}
                                                        alignSelf="flex-start"
                                                    >
                                                        Generate Report
                                                    </Button>
                                                </VStack>
                                            </CardBody>
                                        </Card>
                                    </VStack>
                                </TabPanel>
                            </TabPanels>
                        </Tabs>
                    </VStack>
                </Container>
            </Box>
            <Footer />

            {/* Compliance Report Modal */}
            <Modal isOpen={isOpen} onClose={onClose} size="xl">
                <ModalOverlay
                    backdropFilter="blur(8px)"
                    bg={isDarkTheme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.4)'}
                />
                <ModalContent
                    bg={cardBg}
                    borderWidth="1px"
                    borderColor={isDarkTheme ? borderColor : 'transparent'}
                >
                    <ModalHeader color={headingTextColor}>Compliance Report</ModalHeader>
                    <ModalCloseButton color={strongTextColor} />
                    <ModalBody pb={6}>
                        {complianceReport ? (
                            <VStack spacing={4} align="stretch">
                                <SimpleGrid columns={2} spacing={4}>
                                    <Stat
                                        p={4}
                                        bg={tableBg}
                                        borderRadius="md"
                                        borderWidth="1px"
                                        borderColor={borderColor}
                                    >
                                        <StatLabel color={labelTextColor}>Total Events</StatLabel>
                                        <StatNumber color={strongTextColor}>
                                            {complianceReport.total_events || 0}
                                        </StatNumber>
                                    </Stat>
                                    <Stat
                                        p={4}
                                        bg={tableBg}
                                        borderRadius="md"
                                        borderWidth="1px"
                                        borderColor={borderColor}
                                    >
                                        <StatLabel color={labelTextColor}>Unique Users</StatLabel>
                                        <StatNumber color={strongTextColor}>
                                            {complianceReport.unique_users || 0}
                                        </StatNumber>
                                    </Stat>
                                </SimpleGrid>

                                <Divider borderColor={borderColor} />

                                <Box>
                                    <Heading size="sm" color={headingTextColor} mb={2}>
                                        Period
                                    </Heading>
                                    <Text color={bodyTextColor} fontSize="sm">
                                        From: {complianceReport.period?.from_date}
                                    </Text>
                                    <Text color={bodyTextColor} fontSize="sm">
                                        To: {complianceReport.period?.to_date}
                                    </Text>
                                </Box>

                                {complianceReport.summary && (
                                    <Box>
                                        <Heading size="sm" color={headingTextColor} mb={2}>
                                            Summary
                                        </Heading>
                                        <Text color={bodyTextColor} fontSize="sm">
                                            {JSON.stringify(complianceReport.summary, null, 2)}
                                        </Text>
                                    </Box>
                                )}
                            </VStack>
                        ) : (
                            <Text color={bodyTextColor}>No report data available</Text>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* User Activity Modal */}
            <Modal isOpen={isActivityOpen} onClose={onActivityClose} size="xl">
                <ModalOverlay
                    backdropFilter="blur(8px)"
                    bg={isDarkTheme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.4)'}
                />
                <ModalContent
                    bg={cardBg}
                    borderWidth="1px"
                    borderColor={isDarkTheme ? borderColor : 'transparent'}
                >
                    <ModalHeader color={headingTextColor}>
                        User Activity - {selectedUserId}
                    </ModalHeader>
                    <ModalCloseButton color={strongTextColor} />
                    <ModalBody pb={6}>
                        {loadingActivity ? (
                            <Flex justify="center" py={8}>
                                <Spinner color={primaryColor} />
                            </Flex>
                        ) : userActivity ? (
                            <VStack spacing={4} align="stretch">
                                <SimpleGrid columns={2} spacing={4}>
                                    <Stat
                                        p={4}
                                        bg={tableBg}
                                        borderRadius="md"
                                        borderWidth="1px"
                                        borderColor={borderColor}
                                    >
                                        <StatLabel color={labelTextColor}>Total Actions</StatLabel>
                                        <StatNumber color={strongTextColor}>
                                            {userActivity.total_actions || 0}
                                        </StatNumber>
                                    </Stat>
                                    <Stat
                                        p={4}
                                        bg={tableBg}
                                        borderRadius="md"
                                        borderWidth="1px"
                                        borderColor={borderColor}
                                    >
                                        <StatLabel color={labelTextColor}>Last Active</StatLabel>
                                        <StatNumber fontSize="md" color={strongTextColor}>
                                            {userActivity.last_activity
                                                ? formatDate(userActivity.last_activity)
                                                : 'N/A'}
                                        </StatNumber>
                                    </Stat>
                                </SimpleGrid>

                                {userActivity.activity_summary && (
                                    <Box>
                                        <Heading size="sm" color={headingTextColor} mb={2}>
                                            Activity Summary
                                        </Heading>
                                        <VStack align="stretch" spacing={2}>
                                            {Object.entries(userActivity.activity_summary).map(([key, value]) => (
                                                <Flex key={key} justify="space-between" p={2} bg={tableBg} borderRadius="md">
                                                    <Text color={labelTextColor} fontSize="sm">
                                                        {key}
                                                    </Text>
                                                    <Badge colorScheme="blue">{value}</Badge>
                                                </Flex>
                                            ))}
                                        </VStack>
                                    </Box>
                                )}
                            </VStack>
                        ) : (
                            <Text color={bodyTextColor}>No activity data available</Text>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* Employee List Modal */}
            <Modal isOpen={isEmployeeListOpen} onClose={onEmployeeListClose} size="xl">
                <ModalOverlay
                    backdropFilter="blur(8px)"
                    bg={isDarkTheme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.4)'}
                />
                <ModalContent
                    bg={cardBg}
                    borderWidth="1px"
                    borderColor={isDarkTheme ? borderColor : 'transparent'}
                    maxH="80vh"
                >
                    <ModalHeader color={headingTextColor}>
                        <Icon as={FaUser} mr={2} />
                        Employee List
                    </ModalHeader>
                    <ModalCloseButton color={strongTextColor} />
                    <ModalBody pb={6}>
                        <VStack spacing={4} align="stretch">
                            <Text color={bodyTextColor} fontSize="sm">
                                Click on any User ID to copy it to your clipboard
                            </Text>

                            {/* Search input */}
                            <Input
                                placeholder="Search employees..."
                                value={employeeSearchTerm}
                                onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                                bg={inputBg}
                                color={strongTextColor}
                                borderColor={borderColor}
                                size="sm"
                            />

                            {loadingEmployees ? (
                                <Flex justify="center" py={8}>
                                    <Spinner color={primaryColor} />
                                </Flex>
                            ) : employees.length > 0 ? (
                                filteredEmployees.length > 0 ? (
                                <Box maxH="400px" overflowY="auto">
                                    <VStack align="stretch" spacing={2}>
                                        {filteredEmployees
                                            .map((employee, index) => (
                                                <Card
                                                    key={employee.mlUserId || index}
                                                    size="sm"
                                                    bg={tableBg}
                                                    cursor="pointer"
                                                    onClick={() => {
                                                        if (employee.mlUserId) {
                                                            handleFilterChange('user_id', employee.mlUserId);
                                                            copyToClipboard(employee.mlUserId);
                                                        }
                                                    }}
                                                    _hover={{
                                                        bg: tableHoverBg,
                                                        borderColor: primaryColor,
                                                    }}
                                                    transition="all 0.2s"
                                                    borderWidth="1px"
                                                    borderColor={borderColor}
                                                >
                                                    <CardBody p={3}>
                                                        <HStack justify="space-between">
                                                            <VStack align="start" spacing={0}>
                                                                <Text
                                                                    fontSize="sm"
                                                                    fontWeight="semibold"
                                                                    color={strongTextColor}
                                                                >
                                                                    {employee.name || employee.email || 'Unknown'}
                                                                </Text>
                                                                {employee.email && (
                                                                    <Text fontSize="xs" color={mutedTextColor}>
                                                                        {employee.email}
                                                                    </Text>
                                                                )}
                                                                <HStack mt={1}>
                                                                    <Badge colorScheme="blue" fontSize="xs">
                                                                        ID: {employee.mlUserId || 'N/A'}
                                                                    </Badge>
                                                                    {employee.role && (
                                                                        <Badge colorScheme="green" fontSize="xs">
                                                                            {employee.role}
                                                                        </Badge>
                                                                    )}
                                                                </HStack>
                                                            </VStack>
                                                            <Button
                                                                size="xs"
                                                                variant="ghost"
                                                                color={primaryColor}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (employee.mlUserId) {
                                                                        copyToClipboard(employee.mlUserId);
                                                                    }
                                                                }}
                                                            >
                                                                Copy ID
                                                            </Button>
                                                        </HStack>
                                                    </CardBody>
                                                </Card>
                                            ))}
                                    </VStack>
                                </Box>
                                ) : (
                                    <EmptyStateCard
                                        icon={FaUser}
                                        title={employeeSearchTerm ? 'No employees match this search' : 'No employees available yet'}
                                        description={
                                            employeeSearchTerm
                                                ? `We could not find any employee matching "${employeeSearchTerm}".`
                                                : 'Once employee accounts are created, you can filter audit logs by user directly from this list.'
                                        }
                                        hint={
                                            employeeSearchTerm
                                                ? 'Try searching by name, email, or ML user ID.'
                                                : 'Add team members from Settings to make user-level auditing easier.'
                                        }
                                        bg={tableBg}
                                        borderColor={borderColor}
                                        titleColor={headingTextColor}
                                        descriptionColor={bodyTextColor}
                                        hintColor={mutedTextColor}
                                        iconColor={isDarkTheme ? accentColor : primaryColor}
                                        maxW="full"
                                    >
                                        {employeeSearchTerm ? (
                                            <Button
                                                variant="outline"
                                                borderColor={primaryColor}
                                                color={primaryColor}
                                                _hover={{ bg: tableHoverBg }}
                                                onClick={() => setEmployeeSearchTerm('')}
                                            >
                                                Clear search
                                            </Button>
                                        ) : (
                                            <Button
                                                bg={primaryColor}
                                                color="white"
                                                _hover={{ bg: buttonHoverBg }}
                                                onClick={fetchEmployees}
                                            >
                                                Refresh employees
                                            </Button>
                                        )}
                                    </EmptyStateCard>
                                )
                            ) : (
                                <EmptyStateCard
                                    icon={FaUser}
                                    title="No employees available yet"
                                    description="Once employee accounts are created, you can filter audit logs by user directly from this list."
                                    hint="Add team members from Settings to make user-level auditing easier."
                                    bg={tableBg}
                                    borderColor={borderColor}
                                    titleColor={headingTextColor}
                                    descriptionColor={bodyTextColor}
                                    hintColor={mutedTextColor}
                                    iconColor={isDarkTheme ? accentColor : primaryColor}
                                    maxW="full"
                                >
                                    <Button
                                        bg={primaryColor}
                                        color="white"
                                        _hover={{ bg: buttonHoverBg }}
                                        onClick={fetchEmployees}
                                    >
                                        Refresh employees
                                    </Button>
                                </EmptyStateCard>
                            )}
                        </VStack>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
}

export default AuditCompliance;
